import { createSelector } from 'reselect';
import { isEmpty } from 'lodash/fp';

// Utils
import { asObject } from '../../../../../common/utils/immutableHelper';
import contentSearch from '../../../../components/search/contentSearch/contentSearch';

// Selectors
import { getCurrentBoard, getCurrentBoardId } from '../../../../reducers/currentBoardId/currentBoardIdSelector';
import { getCurrentBoardVisibleDescendantOriginalElements } from '../../../../element/selectors/currentBoardSelector';
import { getSortedViewableViewedBoardIds } from '../../../../user/navigationHistory/navigationHistorySelector';

import { popupSelector } from '../../../../components/popupPanel/popupOpenSelector';
import { getGridSize } from '../../../../utils/grid/gridSizeSelector';
import {
    getCurrentUserLastViewedBoardId,
    getCurrentUserQuickNotesRootBoardId,
    getCurrentUserRootBoardId,
} from '../../../../user/currentUserSelector';
import { getAllComments } from '../../../../element/comment/store/commentsSelector';
import { createDeepSelector, createShallowSelector } from '../../../../utils/milanoteReselect/milanoteReselect';
import { getQuickNotesElementsDeep } from '../quickNotes/quickNotesElementsSelector';

import { SEARCH_RESULT_TYPE } from './elementSearchConstants';

// Selectors for executing a search
export const quickNotesContentSearchSelector = createShallowSelector(
    getQuickNotesElementsDeep,
    getCurrentUserQuickNotesRootBoardId,
    (state, ownProps) => ownProps.query,
    getAllComments,
    (quickNotesElements, quickNotesBoardId, query, comments) => {
        const matchElementIds = contentSearch({
            query,
            elements: quickNotesElements,
            comments,
        });

        if (!matchElementIds.length) return;

        return {
            id: quickNotesBoardId,
            matchElementIds,
            // Ensure the current board is sorted to the top of the list
            score: Number.MAX_SAFE_INTEGER,
            resultType: SEARCH_RESULT_TYPE.QUICK_NOTES,
            dontNavigateOnSelect: true,
        };
    },
);

export const currentBoardContentSearchSelector = createDeepSelector(
    getCurrentBoardVisibleDescendantOriginalElements,
    (state, ownProps) => ownProps.query,
    getCurrentBoardId,
    getCurrentBoard,
    getAllComments,
    (elements, query, currentBoardId, currentBoard, comments) => {
        const matchElementIds = contentSearch({
            query,
            elements: elements.set(currentBoardId, currentBoard),
            comments,
        });

        if (!matchElementIds.length) return;

        return {
            id: currentBoardId,
            matchElementIds,
            // Ensure the current board is sorted to the top of the list
            score: Number.MAX_SAFE_INTEGER,
            resultType: SEARCH_RESULT_TYPE.CURRENT_BOARD,
        };
    },
);
// Selector for the popup state
const getSearchPopup = popupSelector('elementSearch');

const popupStateSelector = createSelector(
    getSearchPopup,
    getSortedViewableViewedBoardIds,
    getCurrentUserLastViewedBoardId,
    getCurrentUserRootBoardId,
    (popup, viewedBoardIds, lastViewedBoardId, rootBoardId) => {
        const searchQuery = popup.get('query');

        const searchResultsList = isEmpty(searchQuery)
            ? // If there's no search query, just use the recently viewed boards, remove the current board
              viewedBoardIds.reduce((acc, id) => {
                  if (id === lastViewedBoardId || id === rootBoardId) return acc;
                  acc.push({ id, score: 1 });
                  return acc;
              }, [])
            : asObject(popup.get('searchResultsList'));

        return {
            loading: popup.get('loading'),
            searchQuery,
            results: popup.get('results'),
            resultsTotal: popup.get('resultsTotal'),
            searchResultsList,
            error: popup.get('error'),
            hasMoreResults: popup.get('resultsTotal') > popup.get('results').size,
        };
    },
);

const searchPopupSelector = createSelector(
    popupStateSelector,
    getCurrentBoardId,
    getCurrentUserQuickNotesRootBoardId,
    getGridSize,
    (popupState, currentBoardId, quickNotesBoardId, gridSize) => ({
        ...popupState,
        currentBoardId,
        quickNotesBoardId,
        gridSize,
    }),
);

export default searchPopupSelector;
